/**
 * Events API
 * 
 * Handles event creation, management, and RSVP functionality.
 */

import { apiGet, apiPost, apiPatch, apiDelete, API_BASE_URL, ApiError } from '../api';
import { guardedFetch } from '../accountGuard';
import { apiUpload, type UploadRequestOptions, type ApiRequestOptions } from '../apiTransport';
import type { UploadableImagePart } from './images';

// Re-exported for consumers of the event-hero upload surface (#2358) — the
// method signatures below take this type, so callers (native hook + tests)
// can import it from here without reaching into './images'.
export type { UploadableImagePart } from './images';

// Check-in method enum (#1117)
export type CheckinMethod = 'gps' | 'secret_word' | 'qr_code' | 'manual';

// Edit scope for recurring events (#1136, #1156)
export type EditScope = 'this_only' | 'this_and_future';

// Delete scope for recurring events (#1203)
export type DeleteScope = EditScope;

// Virtual platform (#1369): platforms that support real-time shared sessions
export type VirtualPlatform = 'zwift' | 'trainingpeaks' | 'rouvy' | 'mywhoosh' | 'other';

export interface Event {
  id: string;
  short_code: string; // Issue #729: Human-friendly URL identifier
  club_id: string;
  training_group_id: string | null; // #1156: For edit scope modal
  club_slug: string | null; // Denormalized for easy club lookup
  title: string;
  description: string | null;
  starts_at: number; // Unix timestamp in seconds
  ends_at: number | null;
  location_name: string | null;
  location_address: string | null;
  location_lat: number | null;
  location_lng: number | null;
  meeting_mode: 'in_person' | 'virtual'; // #1366: Virtual vs in-person events
  virtual_platform: VirtualPlatform | null; // #1369
  virtual_platform_other: string | null; // #1369
  virtual_leader_username: string | null; // #1369
  distance_m: number | null; // Distance in meters (canonical unit - Issue #1083)
  elevation_gain_m: number | null; // Elevation gain in meters (#1159)
  check_ins_enabled: boolean; // Feature toggle from #206 - enables/disables check-ins for this event
  checkin_method: CheckinMethod; // #1117: GPS, secret word, QR code, or manual
  checkin_radius_m: number | null;
  checkin_window_before_min: number | null;
  checkin_window_after_min: number | null;
  secret_word: string | null; // Only used when checkin_method='secret_word'
  event_number: number | null; // #1509: Sequential run number (club-level)
  is_recurring: number; // 0 or 1 (SQLite boolean). #2481: GET /events/:code omitted this entirely until round 7 — see canExportSeriesRecurrence.
  hero_image_url: string | null; // Event-specific hero image with fallback to club image
  card_image_url: string | null; // #1962: server-computed card-sized variant (480x240) of hero_image_url for EventCard thumbnails
  visibility: 'public' | 'members_only'; // Issue #427: Event visibility control
  event_room_id: string | null; // #1279: Chat room for this event
  series_room_id: string | null; // #1279: Chat room for recurring series
  /**
   * The event's creator (`users.id`).
   *
   * #2481: this field was declared as `created_by` for months, but no endpoint
   * has ever returned that key — the column is `owner_id`
   * (`0001_initial_schema.sql:166`, commented "users.id (replaced created_by)")
   * and the detail SELECT returns `e.owner_id` verbatim
   * (`backend/src/routes/events.ts:414`). `grep -rn 'created_by' backend/src/`
   * matches nothing, and there is no `AS created_by` alias anywhere. So every
   * `event.created_by === user.id` check was comparing `undefined` and was
   * permanently false.
   *
   * Renamed rather than aliased on the server: the wire has always said
   * `owner_id`, so the TYPE was the thing that was wrong.
   */
  owner_id: string;
  created_at: number;
  updated_at: number;
}

/**
 * Event Route (GPX import)
 * Issue: #579
 */
export interface EventRoute {
  id: string;
  event_id: string;
  source_file_name: string;
  distance_m: number | null; // Distance in meters (canonical unit - Issue #1083)
  elevation_gain_meters: number | null;
  polyline: string;
  coordinates: [number, number][]; // [lng, lat] GeoJSON order
  origin: { // Route starting point (renamed from start - Issue #1083)
    lat: number;
    lng: number;
  };
  bbox: {
    min_lat: number;
    min_lng: number;
    max_lat: number;
    max_lng: number;
  };
  route_map_url: string | null; // #1076: Pre-rendered route map for share cards
  created_at: number;
  updated_at: number;
}

export interface EventWithRSVP extends Event {
  rsvp_status: 'yes' | 'no' | 'maybe' | null;
  rsvp_count: number;
  // Additional fields for UI
  name?: string; // Alias for title
  club_name?: string; // Denormalized club name
  club_avatar?: string | null; // Denormalized club avatar
  club_hero_image_url?: string | null; // Issue #1105: Club hero for share modal fallback
  training_group_name?: string | null; // #2089: Denormed for training-group badge on event cards
  is_user_attending?: boolean; // Computed from rsvp_status
  // hero_image_url inherited from Event interface
  activity_type?: string | null; // Activity category
  club_visibility?: 'public' | 'private' | 'members_only'; // Issue #427: Club visibility for validation
  route?: EventRoute | null; // Issue #579: GPX route data
  /**
   * #3561: true iff the signed-in viewer has ANY check-in at ANY event of this
   * event's club. Returned ONLY by `GET /events/:code` and ONLY to an
   * authenticated viewer (that response is `Cache-Control: private, no-store`);
   * the anonymous, public-cacheable response omits the key entirely, and no
   * list endpoint sends it. Optional for that reason: consumers must treat
   * `undefined` as "unknown" (the "First time?" panel requires a strict
   * `=== false`).
   */
  viewer_has_checked_in_at_club?: boolean;
  /**
   * #3751: true iff the signed-in viewer has a check-in at THIS event. Same
   * delivery as `viewer_has_checked_in_at_club`: only `GET /events/:code`, only
   * to an authenticated viewer (private, no-store); absent on the anonymous
   * response and on every list endpoint. CheckinButton shows "Checked In!"
   * when it is true and the button has no local state of its own.
   */
  viewer_has_checked_in?: boolean;
}

export interface CreateEventPayload {
  title: string;
  description?: string;
  starts_at: number; // Unix timestamp in seconds
  hero_image_url?: string; // Optional event hero image URL
  ends_at?: number;
  location_name?: string;
  location_address?: string;
  location_lat?: number;
  location_lng?: number;
  distance_m?: number; // Distance in meters (#1159)
  elevation_gain_m?: number; // Elevation gain in meters (#1159)
  check_ins_enabled?: boolean;
  checkin_method?: CheckinMethod; // #1117
  checkin_radius_m?: number;
  checkin_window_before_min?: number;
  checkin_window_after_min?: number;
  secret_word?: string; // Required when checkin_method='secret_word'
  visibility?: 'public' | 'members_only'; // Issue #427: Event visibility control
  meeting_mode?: 'in_person' | 'virtual'; // #1366
  virtual_platform?: VirtualPlatform; // #1369, #2171: narrowed from string
  virtual_platform_other?: string; // #1369
  virtual_leader_username?: string; // #1369
}

export interface UpdateEventPayload {
  title?: string;
  description?: string;
  starts_at?: number;
  ends_at?: number;
  meeting_mode?: 'in_person' | 'virtual'; // #1366
  virtual_platform?: VirtualPlatform; // #1369, #2171: narrowed from string
  virtual_platform_other?: string; // #1369
  virtual_leader_username?: string; // #1369
  location_name?: string;
  location_address?: string;
  location_lat?: number;
  location_lng?: number;
  distance_m?: number; // Distance in meters (#1159)
  elevation_gain_m?: number; // Elevation gain in meters (#1159)
  check_ins_enabled?: boolean;
  checkin_method?: CheckinMethod; // #1117
  checkin_radius_m?: number;
  checkin_window_before_min?: number;
  checkin_window_after_min?: number;
  visibility?: 'public' | 'members_only'; // Issue #427: Event visibility control
  secret_word?: string; // Required when checkin_method='secret_word'
  hero_image_url?: string; // Optional event hero image URL
  edit_scope?: EditScope; // #1156: For recurring event edits
}

// #1160: Batch event creation for repeating events
// Import and re-export DayOfWeek from trainingGroups to avoid duplicate
import type { DayOfWeek } from './trainingGroups';
export type { DayOfWeek };

export interface RepeatPattern {
  frequency: 'weekly' | 'daily';
  days: DayOfWeek[];
  endCondition: 'occurrences' | 'date' | 'never';
  occurrences?: number;
  endDate?: string; // YYYY-MM-DD format
}

export interface CreateBatchEventsPayload {
  title: string;
  description?: string;
  starts_at: number; // Unix timestamp in seconds
  duration_minutes: number;
  meeting_mode?: 'in_person' | 'virtual'; // #1366: Virtual vs in-person events
  virtual_platform?: VirtualPlatform; // #1369
  virtual_platform_other?: string; // #1369
  virtual_leader_username?: string; // #1369
  location_name?: string;
  location_address?: string;
  location_lat?: number;
  location_lng?: number;
  distance_m?: number;
  elevation_gain_m?: number;
  check_ins_enabled?: boolean;
  checkin_method?: CheckinMethod;
  checkin_radius_m?: number;
  checkin_window_before_min?: number;
  checkin_window_after_min?: number;
  secret_word?: string;
  visibility?: 'public' | 'members_only';
  hero_image_url?: string;
  repeat: RepeatPattern;
  timezone?: string;
}

export interface RSVPPayload {
  status: 'yes' | 'no' | 'maybe';
  pace_group_id?: string | null; // #541: Optional pace group selection
}

// ============================================================================
// Pace Groups (#541 - v1.2)
// ============================================================================

export interface PaceGroup {
  id: string;
  event_id: string;
  name: string;
  pace: string | null;
  description: string | null;
  max_participants: number | null;
  display_order: number;
  created_at: number;
  participant_count: number; // Current count of 'yes' RSVPs
}

export interface CreatePaceGroupPayload {
  name: string;
  pace?: string;
  description?: string;
  max_participants?: number;
}

export interface UpdatePaceGroupPayload {
  name?: string;
  pace?: string;
  description?: string;
  max_participants?: number | null;
  display_order?: number;
}

export interface PaceGroupsResponse {
  pace_groups: PaceGroup[];
}

export interface PaceGroupResponse {
  pace_group: PaceGroup;
}

export interface EventFilterParams {
  from?: number;
  to?: number;
}

// ============================================================================
// Event Series (#1208 - v1.2)
// ============================================================================

/**
 * Series summary for Manage Events page
 */
export interface EventSeries {
  series_id: string;
  title: string;
  location_name: string | null;
  recurrence_pattern: string; // "Wed @ 6:30 AM"
  next_occurrence: number; // Unix timestamp (starts_at of next instance)
  next_ends_at: number | null; // #1782: ends_at of next instance (or null)
  next_rsvp_count: number; // RSVPs for NEXT instance only
  upcoming_count: number; // Number of upcoming instances
}

/**
 * The row the GET /clubs/:id/manage-events handler ACTUALLY sends for an event
 * entry (#2142 r7, Rule 55; enriched #3443). A `Pick` of the columns the
 * handler SELECTs and its consumers read (field TYPES track EventWithRSVP).
 *
 * #3443 enriched the handler SELECT (backend/src/routes/events.ts) with the
 * FIVE fields the shared native `EventCard` reads: `distance_m` (plain events
 * column), `hero_image_url` (COALESCE event/club), `card_image_url` (derived
 * variant), the caller's own `rsvp_status`, and `training_group_name` — the
 * same join/subquery/column shapes the member-facing list handler uses — so the
 * shared native `EventCard` (`mobile/src/components/EventCard.tsx`) renders the
 * distance, hero image, RSVP pill, and TG badge on the manage list instead of
 * `undefined`. `ManageRow.event` below is now this type, so the type stops
 * promising fields the server never sends (`club_slug`, `check_ins_*`, virtual
 * fields, room ids, `owner_id`/timestamps, etc.). `distance_m` is nullable —
 * `EventCard` hides the distance row when it is null/0.
 */
export type ManageEventRow = Pick<
  EventWithRSVP,
  | 'id'
  | 'short_code'
  | 'title'
  | 'starts_at'
  | 'ends_at'
  | 'location_name'
  | 'meeting_mode'
  | 'visibility'
  | 'rsvp_count'
  | 'event_number'
  | 'is_recurring'
  | 'training_group_id'
  | 'distance_m'
  | 'hero_image_url'
  | 'card_image_url'
  | 'rsvp_status'
> & {
  // #3455 (George I4): the manage SELECT's `tg.name as training_group_name`
  // is a SELECTed column — always present in the row object (null when the
  // event has no training group). Required here, NOT the optional `?` that
  // EventWithRSVP inherits for its other-endpoint callers, so a dropped column
  // fails the mobile contract. `meeting_mode` above is likewise a real SELECT
  // column (`e.meeting_mode`, backend/src/routes/events.ts:3237) — a type
  // correction, not a new promise.
  training_group_name: string | null;
  // #3841: block-filtered 'maybe' count, the sibling of rsvp_count ('yes' only).
  maybe_count: number;
};

/**
 * Unified manage row - either a series or standalone event.
 * #3443: the event variant is `ManageEventRow` (the true handler envelope),
 * not the broad `EventWithRSVP`.
 */
export type ManageRow =
  | { type: 'series'; series: EventSeries }
  | { type: 'event'; event: ManageEventRow };

export interface ManageEventsResponse {
  rows: ManageRow[];
}

/**
 * Series detail with instances
 */
export interface SeriesDetail {
  series_id: string;
  title: string;
  description: string | null;
  location_name: string | null;
  location_address: string | null;
  recurrence_pattern: string;
  visibility: 'public' | 'members_only';
  upcoming_count: number;
  past_count: number;
}

export interface SeriesInstance {
  id: string;
  short_code: string;
  title: string;
  starts_at: number;
  ends_at: number | null;
  location_name: string | null;
  visibility: string;
  rsvp_count: number;
}

export interface SeriesDetailResponse {
  series: SeriesDetail;
  upcoming_instances: SeriesInstance[];
  past_instances: SeriesInstance[];
}

/**
 * Delete scope for series deletion
 */
export type SeriesDeleteScope = 'next' | 'all_future';

export interface ClubForEventCreation {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  visibility: 'public' | 'private' | 'members_only';
  subscription_tier: 'free' | 'essential' | 'advanced'; // #971
  my_role: 'owner' | 'leader';
  member_count: number;
}

export interface ClubsForEventCreationResponse {
  clubs: ClubForEventCreation[];
}

export interface CreateEventResponse {
  event: Event;
}

export interface UploadEventImageResponse {
  image_url: string;
}

/**
 * Response envelope from `POST /upload/event-hero-image/:eventId`
 * (`backend/src/routes/upload.ts` event-hero POST handler). NOTE: this is the
 * dedicated event-hero handler's shape — `{ success, image_url, message }` —
 * NOT the legacy `UploadEventImageResponse { image_url }` above, which
 * under-types the same envelope. Pinned to the real handler (Rule 55).
 */
export interface EventHeroUploadResponse {
  success: boolean;
  image_url: string;
  message: string;
}

/** Response envelope from `DELETE /upload/event-hero-image/:eventId` (same handler file). */
export interface EventHeroRemoveResponse {
  success: boolean;
  message: string;
}

/**
 * A check-in row from GET /events/:id/checkins (admin/leader only).
 * Backend returns a BARE ARRAY (no envelope). Per Core Rule 12 the users
 * table has no name column, so identity comes as first/last/public_name_mode.
 */
export interface EventCheckin {
  id: string;
  event_id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  public_name_mode: string | null;
  profile_image_url: string | null;
  checked_in_at: number;
  source: string;
}

/** Response from POST /events/:id/checkins/admin */
export interface AdminCheckinResponse {
  checkin: {
    id: string;
    event_id: string;
    user_id: string;
    checked_in_at: number;
    source: string;
  };
}

/**
 * Response from POST /events/:id/checkins (self check-in: gps / qr /
 * secret_word). Owned here so the web and native check-in hooks share one
 * declaration instead of re-declaring it inline on each side (#2150).
 */
export interface CheckinResponse {
  checkin: {
    id: string;
    event_id: string;
    user_id: string;
    checked_in_at: number;
    source: string;
  };
}

export const eventsApi = {
  /**
   * Get clubs where user can create events
   * GET /events/clubs-for-creation
   */
  getClubsForEventCreation: () => 
    apiGet<ClubsForEventCreationResponse>('/events/clubs-for-creation'),

  /**
   * List events (optionally filtered by club)
   * GET /events?club_id={clubId}
   *
   * Handler returns the `{ events: [...] }` envelope (backend events.ts GET
   * /events, both auth branches) — unlike the bare-array sibling
   * GET /clubs/:clubId/events. Unwrapped here so callers get the array (#2326).
   */
  listEvents: (clubId?: string) => {
    const params = clubId ? `?club_id=${clubId}` : '';
    return apiGet<{ events: EventWithRSVP[] }>(`/events${params}`).then((r) => r.events);
  },

  /**
   * Get events for a specific club with optional date filtering
   * GET /clubs/:clubId/events?from={from}&to={to}
   */
  getClubEvents: (clubId: string, filter?: EventFilterParams) => {
    let url = `/clubs/${clubId}/events`;
    const params = new URLSearchParams();
    
    if (filter?.from) {
      params.append('from', filter.from.toString());
    }
    if (filter?.to) {
      params.append('to', filter.to.toString());
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    return apiGet<EventWithRSVP[]>(url);
  },

  /**
   * Get single event by ID
   * GET /events/:id
   * Returns event and optional route data (Issue #963)
   */
  getEvent: (id: string) => apiGet<{ event: EventWithRSVP; route?: EventRoute | null }>(`/events/${id}`),

  /**
   * List check-ins for an event (admin/leader only). Bare-array response.
   * GET /events/:id/checkins
   */
  getCheckins: (id: string) => apiGet<EventCheckin[]>(`/events/${id}/checkins`),

  /**
   * Admin/leader manual check-in of a club member.
   * POST /events/:id/checkins/admin — body { user_id }. Idempotency-keyed.
   */
  adminCheckin: (id: string, userId: string, idempotencyKey: string) =>
    apiPost<AdminCheckinResponse>(
      `/events/${id}/checkins/admin`,
      { user_id: userId },
      { idempotencyKey }
    ),

  /**
   * Create new event
   * POST /clubs/:clubId/events
   *
   * Accepts an optional `X-Idempotency-Key` (#3170): a create is non-idempotent,
   * so a retry storm on a slow/failed response would otherwise write duplicate
   * events. The native hook passes ONE stable key per logical submission — held
   * across a same-payload ambiguous retry so the server can collapse it, rotated
   * on a definite outcome or when the payload changes (#3179). Web callers may
   * omit it. Additive, matching the `createClub` precedent.
   *
   * SCOPE (#3181): the key engages the server's idempotency, but the server-side
   * protection is not yet complete — batch create is not transactional (a partial
   * 5xx can duplicate a series), and Layer-3 DB dedup keys on title+starts_at (an
   * edited-but-same-title retry returns the prior event). Those are tracked server
   * fixes, not a client contract this key alone can guarantee.
   */
  createEvent: (clubId: string, payload: CreateEventPayload, options?: ApiRequestOptions) =>
    apiPost<{ event: Event }>(`/clubs/${clubId}/events`, payload, options),

  /**
   * Create batch events (repeating)
   * POST /clubs/:clubId/events/batch
   * Issue #1160
   *
   * Same idempotency contract as `createEvent` (#3170).
   */
  createBatchEvents: (clubId: string, payload: CreateBatchEventsPayload, options?: ApiRequestOptions) =>
    apiPost<{ created: number; events: { id: string; short_code: string; starts_at: number }[] }>(
      `/clubs/${clubId}/events/batch`,
      payload,
      options
    ),

  /**
   * Update event
   * PATCH /events/:id
   *
   * Handler returns `{ event }` (backend events.ts PATCH /events/:id), not a
   * bare Event. No caller reads the response body today (all invalidate-only),
   * but the declared type must match the wire shape (#2326 Rule 55 audit).
   */
  updateEvent: (id: string, payload: UpdateEventPayload) =>
    apiPatch<{ event: Event }>(`/events/${id}`, payload),

  /**
   * Delete event
   * DELETE /events/:id
   * @param deleteScope - For recurring events: 'this_only' or 'this_and_future' (#1203)
   */
  deleteEvent: (id: string, deleteScope?: DeleteScope) => {
    const url = deleteScope
      ? `/events/${id}?delete_scope=${deleteScope}`
      : `/events/${id}`;
    return apiDelete<{ ok: true; deleted_count?: number }>(url);
  },

  /**
   * RSVP to event
   * POST /events/:id/rsvp
   */
  rsvpToEvent: (id: string, payload: RSVPPayload) => 
    apiPost<void>(`/events/${id}/rsvp`, payload),

  /**
   * Upload event hero image
   * POST /upload/event-hero-image/:eventId
   */
  uploadEventHeroImage: async (eventId: string, file: File): Promise<UploadEventImageResponse> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await guardedFetch(`${API_BASE_URL}/upload/event-hero-image/${eventId}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || 'Failed to upload event hero image');
    }

    return response.json() as Promise<UploadEventImageResponse>;
  },

  /**
   * Remove event hero image
   * DELETE /upload/event-hero-image/:eventId
   */
  removeEventHeroImage: async (eventId: string) => {
    const response = await guardedFetch(`${API_BASE_URL}/upload/event-hero-image/${eventId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Removal failed' }));
      throw new Error(error.message || 'Failed to remove event hero image');
    }

    return response.json();
  },

  /**
   * Upload an event hero image through the platform transport (#2358).
   *
   * Unlike `uploadEventHeroImage` above (a pre-transport web-only path: raw
   * fetch + cookie session), this rides `apiUpload` — so on native it gets
   * Bearer auth, X-Client, one-shot 401 refresh-retry, upload progress, and
   * cancel; on web it gets the cookie session. Both hit the SAME dedicated
   * endpoint (event-owner/club-leader-gated, deletes the old R2 object,
   * persists `events.hero_image_url`, returns the CF-transform URL).
   * Exact sibling of `clubsApi.uploadClubHeroAsset` (#2105).
   *
   * The multipart field is `image` only — the handler reads no `type` field;
   * the `hero` variant is fixed server-side.
   *
   * POST /upload/event-hero-image/:eventId
   *
   * @param part - Web File/Blob or RN `{ uri, name, type }` descriptor
   */
  uploadEventHeroAsset: (
    eventId: string,
    part: UploadableImagePart,
    options?: UploadRequestOptions,
  ): Promise<EventHeroUploadResponse> => {
    const formData = new FormData();
    // RN's descriptor isn't a Blob in the TS lib's eyes but is accepted by
    // RN's FormData at runtime; the cast is confined to this one append.
    formData.append('image', part as Blob);
    return apiUpload<EventHeroUploadResponse>(
      `/upload/event-hero-image/${eventId}`,
      formData,
      options,
    );
  },

  /**
   * Remove an event hero image through the platform transport (#2358).
   * Transport sibling of `removeEventHeroImage` above.
   *
   * DELETE /upload/event-hero-image/:eventId
   */
  removeEventHeroAsset: (
    eventId: string,
    options?: ApiRequestOptions,
  ): Promise<EventHeroRemoveResponse> =>
    apiDelete<EventHeroRemoveResponse>(`/upload/event-hero-image/${eventId}`, options),

  /**
   * Upload GPX route for event (#579)
   * POST /events/:eventId/route
   */
  uploadEventRoute: async (eventId: string, file: File): Promise<{ route: EventRoute }> => {
    const formData = new FormData();
    formData.append('gpx_file', file);

    const response = await guardedFetch(`${API_BASE_URL}/events/${eventId}/route`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new ApiError(
        body.error?.message || 'Failed to upload route',
        body.error?.code || 'UPLOAD_FAILED',
        response.status
      );
    }

    return response.json();
  },

  /**
   * Remove route from event (#579)
   * DELETE /events/:eventId/route
   */
  removeEventRoute: async (eventId: string): Promise<{ success: boolean }> => {
    const response = await guardedFetch(`${API_BASE_URL}/events/${eventId}/route`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new ApiError(
        body.error?.message || 'Failed to remove route',
        body.error?.code || 'DELETE_FAILED',
        response.status
      );
    }

    return response.json();
  },

  // ============================================================================
  // Pace Groups (#541 - v1.2)
  // ============================================================================

  /**
   * List pace groups for an event
   * GET /events/:eventId/pace-groups
   */
  getPaceGroups: (eventId: string) =>
    apiGet<PaceGroupsResponse>(`/events/${eventId}/pace-groups`),

  /**
   * Create a pace group for an event
   * POST /events/:eventId/pace-groups
   */
  createPaceGroup: (eventId: string, payload: CreatePaceGroupPayload) =>
    apiPost<PaceGroupResponse>(`/events/${eventId}/pace-groups`, payload),

  /**
   * Update a pace group
   * PATCH /events/:eventId/pace-groups/:paceGroupId
   */
  updatePaceGroup: (eventId: string, paceGroupId: string, payload: UpdatePaceGroupPayload) =>
    apiPatch<PaceGroupResponse>(`/events/${eventId}/pace-groups/${paceGroupId}`, payload),

  /**
   * Delete a pace group
   * DELETE /events/:eventId/pace-groups/:paceGroupId
   */
  deletePaceGroup: (eventId: string, paceGroupId: string) =>
    apiDelete<{ ok: true }>(`/events/${eventId}/pace-groups/${paceGroupId}`),

  // ============================================================================
  // Event Series (#1208 - v1.2)
  // ============================================================================

  /**
   * Get unified manage rows (series + standalone events) for a club
   * GET /clubs/:clubId/manage-events?filter={upcoming|past|all}
   */
  getManageEvents: (clubId: string, filter?: 'upcoming' | 'past' | 'all') => {
    const url = filter
      ? `/clubs/${clubId}/manage-events?filter=${filter}`
      : `/clubs/${clubId}/manage-events`;
    return apiGet<ManageEventsResponse>(url);
  },

  /**
   * Get series detail with instances
   * GET /clubs/:clubId/series/:seriesId
   */
  getSeriesDetail: (clubId: string, seriesId: string) =>
    apiGet<SeriesDetailResponse>(`/clubs/${clubId}/series/${seriesId}`),

  /**
   * Delete series events
   * DELETE /clubs/:clubId/series/:seriesId?scope={next|all_future}
   */
  deleteSeries: (clubId: string, seriesId: string, scope?: SeriesDeleteScope) => {
    const url = scope
      ? `/clubs/${clubId}/series/${seriesId}?scope=${scope}`
      : `/clubs/${clubId}/series/${seriesId}`;
    return apiDelete<{ ok: true; deleted_count: number }>(url);
  },
};

/**
 * Whether "Entire series" can be exported as an RRULE that actually matches
 * the stored series (#2481 rounds 7-8).
 *
 * There are TWO ways an event becomes recurring:
 *
 *   - `training_group_id` — a training-group event
 *   - `is_recurring` — set to 1 by the batch endpoint (`POST
 *     /clubs/:clubId/events/batch`), which links occurrences via
 *     `recurring_series_id` and leaves `training_group_id` NULL
 *
 * Only the FIRST can be exported honestly today, and that is the whole point of
 * this predicate.
 *
 * Both clients synthesize `{ frequency: 'weekly', days: [thisOccurrenceWeekday] }`.
 * For a training group that is a simplification web has always made knowingly.
 * For a batch series it is simply wrong: `RepeatPatternSchema` allows `daily`
 * frequency, multiple weekdays, and a bounded occurrence count — none of which
 * ride on the event row, so a synthesized rule would describe a DIFFERENT
 * series. A daily 8-occurrence batch would export as an endless weekly event.
 *
 * Round 7 widened this to `training_group_id || is_recurring` and, in doing so,
 * turned "batch series never get the prompt" into "batch series get a wrong
 * one" — trading a missing feature for incorrect calendar data. Narrowed back
 * deliberately: the UI says "add every occurrence", so it must not be offered
 * where that is untrue. Batch series are unblocked by #2505, which exposes a
 * real recurrence contract.
 *
 * The `is_recurring` parameter stays in the signature because it is the field
 * #2505 turns on, and because `GET /events/:code` only started returning it in
 * round 7 (it was declared required on `Event` while never being SELECTed — a
 * phantom field).
 */
export function canExportSeriesRecurrence(event: {
  training_group_id: string | null;
  // Optional, not `Pick<Event,…>`: a response shaped before round 7 omits the
  // key entirely, and callers legitimately hold such objects.
  is_recurring?: number | null;
}): boolean {
  return !!event.training_group_id;
}
